import { useState, useRef, useEffect } from "react";
import { Scissors, Play, Clapperboard, Film } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { EditorBody } from "@/components/VideoEditor";

export default function EditorPanel({ projects, reload }) {
  const editable = projects.filter((p) => (p.type === "shortfilm" || p.type === "video") && p.status === "ready");
  const readySongs = projects.filter((p) => p.type === "song" && p.status === "ready" && p.result?.audio_path);
  const [selId, setSelId] = useState(editable[0]?.id || "");
  const [activeIdx, setActiveIdx] = useState(0);
  const videoRef = useRef(null);

  const project = projects.find((p) => p.id === selId);
  const clips = project
    ? project.type === "shortfilm"
      ? (project.scenes || []).filter((s) => s.video_path).map((s) => s.video_path)
      : project.result?.video_path ? [project.result.video_path] : []
    : [];

  useEffect(() => { setActiveIdx(0); }, [selId]);

  const playAll = () => { setActiveIdx(0); setTimeout(() => videoRef.current?.play(), 60); };
  const onEnded = () => {
    if (activeIdx < clips.length - 1) { setActiveIdx((i) => i + 1); setTimeout(() => videoRef.current?.play(), 60); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 fade-up">
      <div className="flex items-center gap-3">
        <Scissors className="w-6 h-6 text-[#FF334B]" />
        <h2 className="font-head text-2xl sm:text-3xl font-bold tracking-tight">Editor de video</h2>
      </div>

      <div className="mfp-card p-4">
        <label className="mono-label">Proyecto</label>
        <select data-testid="editor-project-select" value={selId || ""} onChange={(e) => setSelId(e.target.value)}
          className="mt-2 w-full bg-[#0B0C13] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#FF334B]">
          <option value="">Selecciona un video o cortometraje</option>
          {editable.map((p) => <option key={p.id} value={p.id}>{p.title} · {p.type === "shortfilm" ? "corto" : "video"}</option>)}
        </select>
      </div>

      {!project ? (
        <div className="mfp-card border-dashed p-12 text-center text-[#64748B]">
          <Film className="w-10 h-10 mx-auto mb-3 text-[#334155]" />
          Crea (y espera a que esté listo) un video o cortometraje primero.
        </div>
      ) : (
        <>
          <div className="mfp-card p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="mono-label">Vista previa</span>
              <StatusBadge status={project.status} />
            </div>
            {clips.length ? (
              <video ref={videoRef} src={mediaUrl(clips[activeIdx])} controls onEnded={onEnded}
                className="w-full rounded-xl bg-black" data-testid="editor-preview" />
            ) : (
              <div className="aspect-video rounded-xl bg-black flex items-center justify-center text-[#64748B]">Sin clips renderizados aún</div>
            )}
            {clips.length > 1 && (
              <>
                <button data-testid="editor-play-all" onClick={playAll}
                  className="mt-3 inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border border-white/12 hover:border-[#FF334B] hover:text-[#FF334B] transition-colors">
                  <Play className="w-4 h-4" /> Reproducir todo en secuencia
                </button>
                <div className="flex gap-3 overflow-x-auto pb-2 mt-3">
                  {clips.map((c, i) => (
                    <button key={i} data-testid={`timeline-clip-${i}`} onClick={() => setActiveIdx(i)}
                      className={`shrink-0 w-32 rounded-xl overflow-hidden border-2 transition-colors ${activeIdx === i ? "border-[#FF334B]" : "border-white/10"}`}>
                      <video src={mediaUrl(c)} muted className="w-full aspect-video object-cover bg-black" />
                      <div className="text-xs py-1.5 bg-[#0B0C13]">Escena {i + 1}</div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="mfp-card p-5">
            <div className="flex items-center gap-2 mb-1">
              <Clapperboard className="w-4 h-4 text-[#FF9F2E]" />
              <span className="mono-label">Editar y ensamblar</span>
            </div>
            <p className="text-xs text-[#64748B] mb-4">Recorta cada escena, reordénalas, añade una tarjeta final y exporta en el formato que quieras. Gratis (proceso local).</p>
            <EditorBody project={project} songs={readySongs} onSaved={reload} />
          </div>
        </>
      )}
    </div>
  );
}
